import axios from "axios";
import { AuthErrorCode } from "@myapp/contract";

export const API_URL = process.env.REACT_APP_API_URL;

type SessionExpiredReason = 'archived' | 'session_expired';

// Registered from the app layer — avoids circular dependency (shared can't import features).
// Falls back to a hard redirect if no callback is registered yet.
let onSessionExpired: ((reason?: SessionExpiredReason) => void) | null = null;
export const setOnSessionExpired = (cb: (reason?: SessionExpiredReason) => void) => {
    onSessionExpired = cb;
};

// Single in-flight refresh promise — prevents parallel 401s from triggering multiple refresh calls.
let refreshPromise: Promise<void> | null = null;

// During a deploy nginx answers every request with 503 and serves the static
// maintenance page (nginx/html/maintenance.html) for document requests. An
// already-open SPA only sees its API calls fail, so reload the document —
// nginx then shows the maintenance page itself. The timestamp guard prevents
// a reload loop when a 503 arrives while the flag is already gone (e.g. the
// backend answering 503 on its own right after a restart).
const MAINTENANCE_RELOAD_AT_KEY = 'maintenance-reload-at';
const reloadForMaintenance = () => {
    const lastReloadAt = Number(sessionStorage.getItem(MAINTENANCE_RELOAD_AT_KEY) ?? 0);
    if (Date.now() - lastReloadAt < 15_000) return;
    sessionStorage.setItem(MAINTENANCE_RELOAD_AT_KEY, String(Date.now()));
    window.location.reload();
};

const apiClient = axios.create({
    withCredentials: true,
    baseURL: API_URL
});

// Access token is in httpOnly cookie — browser sends it automatically.
// No Authorization header needed.

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const isRefreshRequest = originalRequest?.url?.includes('auth/refresh');

        if (error.response?.status === 503) {
            reloadForMaintenance();
            return Promise.reject(error);
        }

        // Skip interceptor for refresh endpoint itself — avoids infinite refresh loops.
        if (error.response?.status === 401 && !originalRequest._isRetry && !isRefreshRequest) {
            originalRequest._isRetry = true;
            try {
                // Refresh token is in httpOnly cookie — browser sends it automatically.
                // Server sets new access token cookie in the response.
                if (!refreshPromise) {
                    refreshPromise = axios
                        .post(`${API_URL}/auth/refresh`, {}, { withCredentials: true })
                        .then(() => { refreshPromise = null; })
                        .catch((err) => { refreshPromise = null; throw err; });
                }
                await refreshPromise;
                return apiClient.request(originalRequest);
            } catch (refreshErr) {
                const reason: SessionExpiredReason =
                    (refreshErr as any)?.response?.data?.error === AuthErrorCode.USER_ARCHIVED
                        ? 'archived'
                        : 'session_expired';
                // Refresh failed — clear auth state via store (soft navigation, no hard reload).
                // Falls back to hard redirect if callback not yet registered.
                if (onSessionExpired) {
                    onSessionExpired(reason);
                } else {
                    window.location.href = '/';
                }
            }
        }

        return Promise.reject(error);
    }
);

export default apiClient;
